/**
 * @fileoverview Main TypeScript file for managing the firewall onboarding process.
 * This file defines the firewall onboarding state and methods used to handle step transitions,
 * UI state updates, and redirection during onboarding.
 */

import { OnboardingData, stepMap } from './types';

/* ================================
   Onboarding State Factory
================================ */

/**
 * Creates and returns the firewall onboarding data, initializing state
 * from the server-provided data present in the DOM.
 *
 * @returns {OnboardingData} The onboarding state and its methods.
 */
export default (): OnboardingData => {
    // Get initial data from the Django template (this JSON comes from the /firewall/get-started response)
    const onboardingData = JSON.parse(
        document.getElementById('onboarding-data')?.textContent || '{}'
    );

    // Helper function to get a cookie by name
    const getCookie = (name: string): string | null => {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                // Does this cookie string begin with the name we want?
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    };

    // Get CSRF token from cookie
    const getCsrfToken = (): string => {
        return getCookie('csrftoken') || ''; // Read directly from cookie
    };

    return {
        /* ---------------------------
           State Properties
        ---------------------------- */
        step: onboardingData.current_step || 'learn',
        completed: onboardingData.completed || false,
        github_api_key: onboardingData.github_api_key || '',
        loading: true,
        copied: '',
        hasExistingCodebases: onboardingData.has_codebases || onboardingData.has_projects || false,
        projects: onboardingData.projects || [],
        installComplete: false,
        email: onboardingData.email || '',
        scanComplete: false,
        cliLastAuth: onboardingData.last_cli_auth || '',
        firewallAuthenticated: Boolean(onboardingData.last_cli_auth && onboardingData.last_cli_auth.trim() !== ''),
        firewallConfigured: false,
        localCodebaseSetup: false,
        codebaseAdded: false,
        showCICDSetup: false,
        selectedTab: 'macos-linux',
        installMethod: onboardingData.install_method || 'local',
        _firewallConfigPollingInterval: null as NodeJS.Timeout | null,
        _cliAuthPollingInterval: null as NodeJS.Timeout | null,
        _codebasePollingInterval: null as NodeJS.Timeout | null,
        isTransitioning: false,
        activeCard: 'auth',
        $watch: (_property: string, _callback: (newValue: any, oldValue: any) => void) => {
            console.warn('$watch called but not implemented');
        },
        $dispatch: (event: string, detail?: any) => {
            (this as any).$dispatch(event, detail);
        },
        getCsrfToken,

        /* ----------------------------
           Utility Methods
        ---------------------------- */
        /**
         * Formats a given date string into a human-friendly relative date.
         *
         * @param {string} dateString - The date string to format.
         * @returns {string} A human-friendly date representation.
         */
        formatDate(dateString: string): string {
            if (!dateString) {
                return '';
            }

            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return '';
            }

            const now = new Date();
            // Get midnight for 'now' and 'date' to compare calendar days
            const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const dateMidnight = new Date(date.getFullYear(), date.getMonth(), date.getDate());
            const yesterdayMidnight = new Date(todayMidnight);
            yesterdayMidnight.setDate(todayMidnight.getDate() - 1);


            const isToday = dateMidnight.getTime() === todayMidnight.getTime();
            const isYesterday = dateMidnight.getTime() === yesterdayMidnight.getTime();

            const timeString = date.toLocaleString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });

            if (isToday) {
                return `Today at ${timeString}`;
            } else if (isYesterday) {
                return `Yesterday at ${timeString}`;
            } else {
                // Calculate difference in calendar days using midnight times
                const diffTime = todayMidnight.getTime() - dateMidnight.getTime(); // Difference from today's midnight
                const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)); // Use Math.round for robustness

                if (diffDays > 1 && diffDays <= 7) { // Check diffDays > 1 explicitly
                    return `${diffDays} days ago`;
                } else if (diffDays === 1) {
                     // This case should technically be covered by isYesterday,
                     // but keep it as a fallback, although it might indicate an edge case.
                     // We prioritize the 'Yesterday' string if isYesterday is true.
                     return `1 day ago`;
                } else {
                    // More than 7 days ago, show full date
                    return date.toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                    });
                }
            }
        },

        /**
         * Retrieves the current numeric step based on the stepMap.
         *
         * @returns {number} The current step number.
         */
        getStep(): number {
            return stepMap[this.step] || 1;
        },

        /* ---------------------------
           UI State Update Methods
        ---------------------------- */
        /**
         * Sets the current onboarding step and notifies the backend.
         *
         * @param {string | number} step - The new step (either a step name or a number).
         */
        async setStep(step: string | number): Promise<void> {
            const previousStep = this.step;

            try {
                let newStep: string;
                if (typeof step === 'number') {
                    const reverseMap = Object.entries(stepMap).find(([_, val]) => val === step);
                    newStep = reverseMap ? reverseMap[0] : String(step);
                } else {
                    newStep = String(step);
                }
                this.step = newStep;

                if (newStep === 'secure') {
                    await this.initStep3();
                }

                if (newStep === 'firewall') {
                    await this.initStep4();
                }

                this.loading = false;

                const csrfToken = getCsrfToken();

                const response = await fetch('/firewall/get-started/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({ step: newStep })
                });

                // Check specifically for CSRF error (403)
                if (response.status === 403) {
                    console.error('CSRF verification failed during step update. Refreshing page...');
                    window.location.reload();
                    return; // Prevent further execution
                }

                if (!response.ok) {
                    throw new Error('Failed to update step');
                }
            } catch (error) {
                if (error && typeof error === 'object' && 'isFromCancelledTransition' in error) {
                    return;
                }
                console.error('Error updating step:', error);
                this.step = previousStep;
            }
        },

        /**
         * Copies a given command to the clipboard.
         * Sets the 'copied' state based on the content type.
         *
         * @param {string} command - The command to copy.
         */
        async copyCommand(command: string): Promise<void> {
            await navigator.clipboard.writeText(command);
            this.copied = command.includes('safety scan') ? 'scan' : 'install';
            setTimeout(() => {
                this.copied = '';
            }, 2000);
        },

        /**
         * Advances the onboarding to the next step.
         */
        async nextStep(): Promise<void> {
            const currentStep = this.getStep();

            if (currentStep === 2 && !this.firewallConfigured) {
                if (this.firewallAuthenticated) {
                    this.updateActiveCard();
                }
                return;
            }

            const nextStepMap: { [key: number]: string } = {
                1: 'you',
                2: 'codebases',
                3: 'ai_assistants'
            };

            if (currentStep === 4) {
                return;
            }

            await this.setStep(nextStepMap[currentStep] || 'learn');
        },

        /**
         * Restarts the onboarding process from the beginning.
         */
        async restart(): Promise<void> {
            try {
                this.loading = true;

                const csrfToken = getCsrfToken();

                const response = await fetch('/firewall/get-started/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({ action: 'reset' })
                });

                // Check specifically for CSRF error (403)
                if (response.status === 403) {
                    console.error('CSRF verification failed during restart. Refreshing page...');
                    window.location.reload();
                    return; // Prevent further execution
                }

                if (!response.ok) {
                    throw new Error('Failed to reset onboarding');
                }

                this.step = 'learn';
                this.completed = false;
                this.installComplete = false;
                this.scanComplete = false;

            } catch (error) {
                console.error('Error restarting onboarding:', error);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Skips the onboarding process, marking it as skipped in the backend.
         */
        async skipOnboarding(): Promise<void> {
            try {
                this.loading = true;

                const csrfToken = getCsrfToken();

                const response = await fetch('/firewall/get-started/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({ action: 'skip' })
                });

                // Check specifically for CSRF error (403)
                if (response.status === 403) {
                    console.error('CSRF verification failed during skip. Refreshing page...');
                    window.location.reload();
                    return; // Prevent further execution
                }

                if (!response.ok) {
                    throw new Error('Failed to skip onboarding');
                }

                console.log("Onboarding skipped successfully.");
                // Mark onboarding as complete locally and redirect (if desired)
                this.completed = true;
                window.location.href = '/firewall';
            } catch (error) {
                console.error('Error skipping onboarding:', error);
                this.loading = false;
            }
        },

        /* ---------------------------
           Authentication & Firewall methods
        ---------------------------- */
        /**
         * Sets the firewall authentication state.
         *
         * @param {boolean} value - Whether the firewall is authenticated.
         */
        setFirewallAuthenticated(value: boolean): void {
            if (value && !this.firewallAuthenticated) {
                this.stopCliAuthPolling();
                this.firewallAuthenticated = true;
                this.installComplete = true;
                this.startFirewallConfigPolling();
                console.log('setFirewallAuthenticated: true - State updated, stopped auth polling, started config polling.');
            } else if (!value && this.firewallAuthenticated) {
                this.firewallAuthenticated = false;
                if(this.activeCard === 'waiting') {
                    this.activeCard = 'auth';
                }
                this.stopFirewallConfigPolling();
                console.log('setFirewallAuthenticated: false - State updated, stopped config polling.');
            }
        },

        /**
         * Sets the firewall configuration state.
         *
         * @param {boolean} value - Whether the firewall is configured.
         */
        setFirewallConfigured(value: boolean): void {
            if (value && !this.firewallConfigured) {
                this.stopFirewallConfigPolling();
                this.firewallConfigured = true;
                this.updateActiveCard();
            } else if (!value && this.firewallConfigured) {
                this.firewallConfigured = false;
                this.updateActiveCard();
            }
        },

        /**
         * Sets up the event listener for CLI authentication.
         * This is used to detect when the CLI has been authenticated.
         */
        setupCliAuthenticationListener(): void {
            document.addEventListener('safety-cli-authenticated', ((event: CustomEvent) => {
                console.log('Received safety-cli-authenticated event:', event.detail);
                const eventData = event.detail || {};
                this.cliLastAuth = eventData.last_auth || new Date().toISOString();
                this.setFirewallAuthenticated(true);
            }) as EventListener);

            document.addEventListener('onboarding:firewall-configured', () => {
                console.log('Received onboarding:firewall-configured event');
                this.setFirewallConfigured(true);
            });

            document.addEventListener('onboarding:scan-complete', () => {
                console.log('Received onboarding:scan-complete event');
                this.setScanComplete(true);
            });
        },

        /**
         * Sets the install method.
         *
         * @param {string} method - The install method to set.
         */
        setInstallMethod(method: string): void {
            this.installMethod = method;
        },

        /**
         * Skips the firewall setup process.
         */
        async skipFirewallSetup(): Promise<void> {
            console.log('Skipping firewall setup (auth/config)');
            this.setFirewallAuthenticated(true);
            this.setFirewallConfigured(true);
            await this.setStep('codebases');
        },

        /* ---------------------------
           Codebase methods
        ---------------------------- */

        /**
         * Sets the show CI/CD setup state.
         *
         * @param {boolean} value - Whether to show CI/CD setup.
         */
        setShowCICDSetup(value: boolean): void {
            this.showCICDSetup = value;
        },

        /* ---------------------------
           Step-Specific Methods
        ---------------------------- */
        /**
         * Initializes additional data for the secure (step 3) onboarding step.
         */
        async initStep3(): Promise<void> {
            console.log('initStep3 called. Polling started via init/watch if needed.');
        },

        /**
         * Initializes additional data for the firewall (step 4) onboarding step.
         */
        async initStep4(): Promise<void> {
            if (this.getStep() === 4) {
                this.hasExistingCodebases = this.projects.length > 0;
            }
        },

        /* ---------------------------
           Finalization / Redirection Methods
        ---------------------------- */
        /**
         * Finalizes the onboarding process by marking it as complete and redirecting.
         */
        async finalizeOnboarding(): Promise<void> {
            try {
                const csrfToken = getCsrfToken();

                const response = await fetch('/firewall/get-started/', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': csrfToken,
                    },
                    body: JSON.stringify({ step: 'ai_assistants', completed: true })
                });

                // Check specifically for CSRF error (403)
                if (response.status === 403) {
                    console.error('CSRF verification failed during finalization. Refreshing page...');
                    window.location.reload();
                    return; // Prevent further execution
                }

                if (!response.ok) {
                    throw new Error('Failed to complete onboarding');
                }

                this.completed = true;

                // Redirect based on firewall configuration status
                if (this.firewallConfigured) {
                    window.location.href = '/firewall';
                } else {
                    window.location.href = '/codebases';
                }

            } catch (error) {
                console.error('Error during onboarding finalization:', error);
                this.completed = false;
            }
        },

        /* ---------------------------
           Initialization
        ---------------------------- */
        /**
         * Initializes the onboarding state.
         * Attaches event listeners and sets up websocket if needed.
         * Called automatically by Alpine.js when the component is mounted.
         */
        init(): void {
            this.loading = false;
            this._firewallConfigPollingInterval = null;
            this.setupCliAuthenticationListener();

            if (this.getStep() === 2) {
                if (this.firewallAuthenticated) {
                    this.activeCard = 'waiting';
                } else {
                    this.activeCard = 'auth';
                }
                console.log(`init: Initial activeCard set to '${this.activeCard}' based on auth status.`);
            }

            const currentStepNum = this.getStep();

            if (currentStepNum === 2) {
                if (!this.firewallAuthenticated) {
                    console.log('init: Condition met, starting CLI Auth polling.');
                    this.startCliAuthPolling();
                } else if (!this.firewallConfigured) {
                    console.log('init: Condition met, starting Firewall Config polling.');
                    this.startFirewallConfigPolling();
                } else {
                    console.log('init: Already authenticated and configured. No polling needed.');
                    this.stopCliAuthPolling();
                    this.stopFirewallConfigPolling();
                }
            } else if (currentStepNum === 3) {
                this.stopCliAuthPolling();
                this.stopFirewallConfigPolling();
                this.firewallAuthenticated = true;
                this.firewallConfigured = true;

                if (!this.hasExistingCodebases && !this.scanComplete) {
                    console.log('init: Condition met, starting Codebase polling.');
                    this.startCodebasePolling();
                }
            } else if (currentStepNum === 4) {
                this.stopCliAuthPolling();
                this.stopFirewallConfigPolling();
                this.stopCodebasePolling();
                this.firewallAuthenticated = true;
                this.firewallConfigured = true;
            }

            window.addEventListener('onboarding:codebase-added', () => {
                this.codebaseAdded = true;
                this.stopCodebasePolling();
                this.setScanComplete(true);
            });

            this.$watch('step', (newStep: string, oldStep: string) => {
                console.log(`Step changed from ${oldStep} to ${newStep}`);

                this.stopCliAuthPolling();
                this.stopFirewallConfigPolling();
                this.stopCodebasePolling();

                const nextStepNum = this.getStep();

                if (nextStepNum === 2) {
                    if (this.firewallAuthenticated) {
                        this.activeCard = 'waiting';
                        if (!this.firewallConfigured) {
                            console.log('$watch step: Condition met, starting Firewall Config polling.');
                            this.startFirewallConfigPolling();
                        } else {
                            console.log('$watch step: Already configured on step 2 entry.');
                            this.stopFirewallConfigPolling();
                        }
                    } else {
                        this.activeCard = 'auth';
                        console.log('$watch step: Condition met, starting CLI Auth polling.');
                        this.startCliAuthPolling();
                    }
                    console.log(`$watch step: Set activeCard to '${this.activeCard}' for step 2.`);
                } else if (nextStepNum === 3) {
                    this.firewallAuthenticated = true;
                    this.firewallConfigured = true;
                    if (!this.hasExistingCodebases && !this.scanComplete) {
                        console.log('$watch step: Condition met, starting Codebase polling.');
                        this.startCodebasePolling();
                    }
                } else if (nextStepNum === 4) {
                    this.firewallAuthenticated = true;
                    this.firewallConfigured = true;
                }
            });
        },

        /**
         * Check if user already has firewall configured via API
         * This helps skip the waiting screen if the user already has firewall configured
         */
        async checkFirewallStatus(): Promise<void> {
            console.log('checkFirewallStatus: Interval fired.');
            console.log('Polling for Firewall config status...');
            try {
                const response = await fetch('/firewall/api/user-status/');
                if (!response.ok) {
                    return;
                }

                const data = await response.json();

                if (data.last_installation && !this.cliLastAuth) {
                    this.cliLastAuth = data.last_installation;
                    this.installComplete = true;
                }

                if (this.cliLastAuth || data.last_installation) {
                    this.firewallAuthenticated = true;
                }

                const isInitialAuth = this.step === 'you' && !this.firewallConfigured;
                if (!isInitialAuth && data.is_enabled && data.is_protected) {
                    this.firewallConfigured = true;
                } else {
                    if (this.firewallAuthenticated && !this.firewallConfigured) {
                        this.startFirewallConfigPolling();
                    }
                }
            } catch (error) {
                return;
            }
        },

        /**
         * Starts polling for firewall configuration status
         * Will check every 5 seconds until firewall is configured or max attempts reached
         */
        startFirewallConfigPolling(): void {
            if (this._firewallConfigPollingInterval) {
                clearInterval(this._firewallConfigPollingInterval);
                this._firewallConfigPollingInterval = null;
            }

            let attempts = 0;
            const maxAttempts = 60;

            this._firewallConfigPollingInterval = setInterval(async () => {
                if (this.firewallConfigured || attempts >= maxAttempts) {
                    if (this._firewallConfigPollingInterval) {
                        clearInterval(this._firewallConfigPollingInterval);
                        this._firewallConfigPollingInterval = null;
                    }
                    return;
                }

                attempts++;

                try {
                    const response = await fetch('/firewall/api/user-status/');
                    if (!response.ok) {
                        return;
                    }

                    const data = await response.json();

                    if (data.is_enabled && data.is_protected) {
                        this.firewallConfigured = true;
                        window.dispatchEvent(new CustomEvent('onboarding:firewall-configured'));

                        if (this._firewallConfigPollingInterval) {
                            clearInterval(this._firewallConfigPollingInterval);
                            this._firewallConfigPollingInterval = null;
                        }
                    }
                } catch (error) {
                    return;
                }
            }, 5000);
        },

        startCardTransition(): void {
            this.isTransitioning = true;
        },

        endCardTransition(): void {
            setTimeout(() => {
                this.isTransitioning = false;
            }, 500);
        },

        /**
         * Manually transitions the active card to the 'waiting' state for firewall configuration.
         * Typically called after the user clicks a "Continue" button post-authentication.
         */
        proceedToConfigurationCard(): void {
            if (this.getStep() === 2 && this.firewallAuthenticated && this.activeCard === 'auth') {
                console.log("proceedToConfigurationCard: Triggering transition to 'waiting' card.");
                this.startCardTransition();
                setTimeout(() => {
                    this.activeCard = 'waiting';
                    if (!this.firewallConfigured) {
                        this.startFirewallConfigPolling();
                    }
                    this.endCardTransition();
                }, 300);
            } else {
                console.log("proceedToConfigurationCard: Conditions not met or already on 'waiting' card.");
            }
        },

        updateActiveCard(): void {
            if (this.getStep() !== 2) return;
            if (this.isTransitioning) return;

            console.log(`updateActiveCard: Verifying polling for current card '${this.activeCard}'.`);

            if (this.activeCard === 'waiting') {
                this.stopCliAuthPolling();
                if (!this.firewallConfigured) {
                    if (!this._firewallConfigPollingInterval) {
                        console.log('updateActiveCard (waiting): Ensuring config polling is running.');
                        this.startFirewallConfigPolling();
                    }
                } else {
                    console.log('updateActiveCard (waiting): Ensuring config polling is stopped.');
                    this.stopFirewallConfigPolling();
                }
            } else if (this.activeCard === 'auth') {
                this.stopFirewallConfigPolling();
                if (!this.firewallAuthenticated) {
                    if (!this._cliAuthPollingInterval) {
                        console.log('updateActiveCard (auth): Ensuring auth polling is running.');
                        this.startCliAuthPolling();
                    }
                } else {
                    console.log('updateActiveCard (auth): Authenticated, ensuring auth polling is stopped.');
                    this.stopCliAuthPolling();
                }
            }
        },

        setLocalCodebaseSetup(value: boolean): void {
            this.localCodebaseSetup = value;
        },

        simulateCodebaseAdded(): void {
            this.codebaseAdded = true;
            window.dispatchEvent(new CustomEvent('onboarding:codebase-added'));
        },

        markInstallComplete(): void {
            this.installComplete = true;
        },

        /**
         * Sets the codebase scan completion status and updates polling.
         *
         * @param {boolean} value - True if scan is complete.
         */
        setScanComplete(value: boolean): void {
            if (value && !this.scanComplete) {
                this.scanComplete = true;
                this.stopCodebasePolling();
            } else if (!value && this.scanComplete) {
                this.scanComplete = false;
            }
        },

        /* ---------------------------
           Polling Methods
        ---------------------------- */
        /**
         * Periodically checks the backend for CLI authentication status.
         */
        async pollCliAuthStatus(): Promise<void> {
            console.log('pollCliAuthStatus: Interval fired.');
            console.log('Polling for CLI auth status...');
            try {
                const response = await fetch('/firewall/api/v1/firewall/onboarding-status/');
                if (!response.ok) {
                    console.error('Polling error: Failed to fetch onboarding status', response.status);
                    return;
                }
                const data = await response.json();
                if (data.last_cli_auth) {
                    console.log('Polling successful: CLI authenticated.');
                    this.cliLastAuth = data.last_cli_auth;
                    this.setFirewallAuthenticated(true);
                }
            } catch (error) {
                console.error('Polling exception:', error);
            }
        },

        /**
         * Starts the polling interval for CLI authentication status.
         */
        startCliAuthPolling(): void {
            if (this._cliAuthPollingInterval) return;
            console.log('Starting CLI Auth polling.');
            this.pollCliAuthStatus();
            this._cliAuthPollingInterval = setInterval(() => this.pollCliAuthStatus(), 5000);
        },

        /**
         * Stops the polling interval for CLI authentication status.
         */
        stopCliAuthPolling(): void {
            if (this._cliAuthPollingInterval) {
                console.log('Stopping CLI Auth polling.');
                clearInterval(this._cliAuthPollingInterval);
                this._cliAuthPollingInterval = null;
            }
        },

        /**
         * Stops the polling interval for firewall configuration status.
         */
        stopFirewallConfigPolling(): void {
            if (this._firewallConfigPollingInterval) {
                console.log('Stopping Firewall Config polling.');
                clearInterval(this._firewallConfigPollingInterval);
                this._firewallConfigPollingInterval = null;
            }
        },

        /**
         * Periodically checks the backend for new codebases.
         * Uses the existing /api/v1/codebases/ endpoint.
         */
        async pollCodebaseStatus(): Promise<void> {
            console.log('pollCodebaseStatus: Interval fired.');
            console.log('Polling for codebase status...');
            try {
                const response = await fetch('/api/v1/codebases/');
                if (!response.ok) {
                    console.error('Polling error: Failed to fetch codebases', response.status);
                    return;
                }
                const data = await response.json();

                let projectsList: any[] = [];
                if (data && Array.isArray(data.results)) {
                    projectsList = data.results;
                } else if (Array.isArray(data)) {
                    projectsList = data;
                }

                const projectsExist = projectsList.length > 0;

                if (projectsExist) {
                    console.log('Polling successful: Codebases found:', projectsList);
                    this.projects = projectsList;
                    this.hasExistingCodebases = true;
                    this.stopCodebasePolling();

                    setTimeout(() => {
                        this.setScanComplete(true);
                    }, 50);
                } else {
                    console.log('Polling: No codebases found yet.');
                }
            } catch (error) {
                console.error('Error polling for codebases:', error);
            }
        },

        /**
         * Starts the polling interval for codebase status.
         */
        startCodebasePolling(): void {
            if (this._codebasePollingInterval) return;
            console.log('Starting Codebase polling.');
            this.pollCodebaseStatus();
            this._codebasePollingInterval = setInterval(() => this.pollCodebaseStatus(), 7000);
        },

        /**
         * Stops the polling interval for codebase status.
         */
        stopCodebasePolling(): void {
            if (this._codebasePollingInterval) {
                console.log('Stopping Codebase polling.');
                clearInterval(this._codebasePollingInterval);
                this._codebasePollingInterval = null;
            }
        },
    };
};
